import { useEffect, useMemo, useState } from "react";
import { Select, Form, Input, DatePicker } from "antd";
import { useParams } from "react-router-dom";
import LoadableButton from "../../components/buttons/LoadableButton";
import TableWithSkeleton from "../../components/global/table/TableWithSkeleton";
import Pagination from "../../components/global/pagination/Pagination";
import useColumns from "../../columns/AssignedUsers";
import { GetAllUsersApi } from "../../api/request/settings/notifications";
import { AssignSalesManagerApi, GetTraderByIdApi } from "../../api/request/client";
import { ExportIBHierarchyCommissionLotsApi } from "../../api/request/ibapproved";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/global/modal/ConfirmationModal";
import ModalComponent from "../../components/global/modal/ModalComponent";
import { getDownloadFilenameFromResponse, getIBHierarchyReportFallbackName } from "../../utils/utils";

const { RangePicker } = DatePicker;

const AssignedToSales = () => {
  const { id } = useParams();
  const [form] = Form.useForm();
  const [reportForm] = Form.useForm();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecord, setTotalRecord] = useState(0);

  const [selectUsers, setSelectUsers] = useState([]);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [ibClientType, setIbClientType] = useState("all");
  const [salesAgentEmail, setSalesAgentEmail] = useState("");

  const [isOptionsLoading, setIsOptionsLoading] = useState(false);
  const [isAssignLoading, setIsAssignLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isReportLoading, setIsReportLoading] = useState(false);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchSalesAgentEmail = async () => {
    try {
      const { data } = await GetTraderByIdApi(id);
      if (data?.success) {
        setSalesAgentEmail(data?.data?.email || "");
      }
    } catch (error) {
      // keep empty; user can type manually
    }
  };

  const fetchAllUsers = async () => {
    setIsOptionsLoading(true);
    try {
      const params = new URLSearchParams({
        ApplyPagination: false,
        SalesManId: id,
        ...(ibClientType !== "allClients" ? { UserType: "IB" } : {}),
        ...(ibClientType === "master" ? { IsMasterIB: true } : {}),
      }).toString();
      const { data } = await GetAllUsersApi(params);

      if (!data?.success) return;

      const users = data.data || [];

      setSelectUsers(
        users
          .filter((u) => !u?.isAssignedToSales)
          .map((u) => ({
            value: u.userId,
            label: `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim(),
          })),
      );

      const assigned = users
        .filter((u) => u?.isAssignedToSales)
        .map((u) => ({
          userId: u.userId,
          name: u.name || `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim(),
          email: u.email,
          phoneNo: u.phoneNo,
          countryName: u.countryName,
          salesName: u.salesName,
          userType: u.userType,
          createdDate: u.createdDate,
        }));

      setAssignedUsers(assigned);
      setTotalRecord(assigned.length);

      if (!salesAgentEmail) {
        const emailFromUsers =
          users?.find((u) => u?.salesEmail)?.salesEmail || "";
        if (emailFromUsers) setSalesAgentEmail(emailFromUsers);
      }

      form.resetFields(["userId"]);
    } catch (error) {
      toast.error(
        error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to load users.",
      );
    } finally {
      setIsOptionsLoading(false);
    }
  };

  const handleAssign = async () => {
    try {
      const { userId } = await form.validateFields(["userId"]);

      setIsAssignLoading(true);

      const params = {
        userId,
        salesManagerUserId: id,
      };

      const { data } = await AssignSalesManagerApi(params);

      if (data?.success) {
        toast.success(data.message);
        fetchAllUsers();
      }
    } catch (error) {
      if (error?.errorFields) return;
      toast.error(
        error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to assign users.",
      );
    } finally {
      setIsAssignLoading(false);
    }
  };

  const handleDeleteClick = (record) => {
    setSelectedUser(record);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUser) return;

    try {
      setIsDeleteLoading(true);

      const params = {
        userId: [selectedUser.userId],
        salesManagerUserId: null,
      };

      const { data } = await AssignSalesManagerApi(params);

      if (data?.success) {
        toast.success(data.message || "User unassigned successfully.");
        setIsDeleteModalOpen(false);
        setSelectedUser(null);
        fetchAllUsers();
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to unassign user.",
      );
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const handleOpenReportModal = () => {
    reportForm.setFieldsValue({
      salesAgentEmail: salesAgentEmail || undefined,
      dateRange: undefined,
      masterIb: false,
    });
    setIsReportModalOpen(true);
  };

  const handleCloseReportModal = () => {
    setIsReportModalOpen(false);
    reportForm.resetFields();
  };

  const downloadBlobFile = (blobData, fileName) => {
    const blob = new Blob([blobData], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  };

  const handleGetReport = async (values) => {
    try {
      setIsReportLoading(true);
      const [from, to] = values?.dateRange || [];
      const params = {
        salesAgentEmail: values?.salesAgentEmail,
        fromDate: from ? from.format("YYYY-MM-DD") : "",
        toDate: to ? to.format("YYYY-MM-DD") : "",
        masterIb: values?.masterIb === true,
      };

      const response = await ExportIBHierarchyCommissionLotsApi(
        new URLSearchParams(params).toString(),
      );

      const contentType = response?.headers?.["content-type"] || "";
      if (contentType.includes("application/json")) {
        const text = await response.data.text();
        const parsed = JSON.parse(text);
        toast.error(
          parsed?.message ||
            parsed?.error?.message ||
            "Failed to get report.",
        );
        return;
      }

      downloadBlobFile(
        response.data,
        getDownloadFilenameFromResponse(
          response,
          getIBHierarchyReportFallbackName({
            salesAgentEmail: params.salesAgentEmail,
          }),
        ),
      );
      toast.success("Report downloaded successfully.");
      handleCloseReportModal();
    } catch (error) {
      toast.error(
        error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to get report.",
      );
    } finally {
      setIsReportLoading(false);
    }
  };

  useEffect(() => {
    fetchAllUsers();
  }, [ibClientType]);

  useEffect(() => {
    if (id) fetchSalesAgentEmail();
  }, [id]);

  useEffect(() => {
    setPage(1);
  }, [assignedUsers.length]);

  const pagedAssignedUsers = useMemo(
    () => assignedUsers.slice((page - 1) * pageSize, page * pageSize),
    [assignedUsers, page, pageSize],
  );

  const columns = useColumns(handleDeleteClick);

  return (
    <div>
      <Form form={form} layout="vertical">
        <div className="assigned-to-sales-toolbar flex flex-nowrap items-center justify-end gap-3 mt-3">
          <button
            type="button"
            onClick={handleOpenReportModal}
            className="toolbar-btn bg-primary text-sm text-white font-semibold px-4 rounded-lg hover:opacity-90 duration-300 whitespace-nowrap"
          >
            Get Hierarchy Commission Report
          </button>

          <div className="toolbar-select min-w-[200px]">
            <Select
              className="w-full"
              value={ibClientType}
              options={[
                { label: "All Clients", value: "allClients" },
                { label: "IB Clients", value: "all" },
                { label: "Master IB Clients", value: "master" },
              ]}
              onChange={(value) => setIbClientType(value)}
            />
          </div>

          <div className="toolbar-select min-w-[420px] flex-1 max-w-[560px]">
            <Form.Item
              name="userId"
              className="!mb-0 !leading-none"
              rules={[
                {
                  required: true,
                  message: "Please select at least one user.",
                },
              ]}
            >
              <Select
                mode="multiple"
                showSearch
                allowClear
                maxTagCount="responsive"
                loading={isOptionsLoading}
                options={selectUsers}
                placeholder="Select users to assign"
                className="w-full"
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </div>

          <LoadableButton
            lable="Assign"
            type="button"
            onClick={handleAssign}
            isLoading={isAssignLoading}
            loadingLable="Assigning..."
            className="toolbar-btn bg-primary text-sm text-white font-semibold px-5 rounded-lg hover:opacity-90"
          />
        </div>
      </Form>

      <div className="mt-6">
        <div className="border border-light rounded-xl overflow-hidden bg-white">
          <TableWithSkeleton
            columns={columns}
            data={pagedAssignedUsers}
            loading={isOptionsLoading}
            page={page}
            pageSize={pageSize}
          />
        </div>

        <Pagination
          total={totalRecord}
          pageSize={pageSize}
          setPageSize={setPageSize}
          page={page}
          setPage={setPage}
        />
      </div>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        type="error"
        message="Are you sure you want to unassign this user?"
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleteLoading}
        loadingLabel="Removing..."
      />

      <ModalComponent
        isOpen={isReportModalOpen}
        setIsOpen={setIsReportModalOpen}
        title="Get Hierarchy Commission Report"
        width={560}
      >
        <Form
          form={reportForm}
          layout="vertical"
          onFinish={handleGetReport}
          initialValues={{ masterIb: false }}
          className="w-full"
        >
          <Form.Item
            name="salesAgentEmail"
            label="Sales Agent Email"
            rules={[
              { required: true, message: "Please enter sales agent email." },
              { type: "email", message: "Please enter a valid email." },
            ]}
          >
            <Input size="large" placeholder="Enter sales agent email" />
          </Form.Item>

          <div style={{ marginTop: 24 }}>
            <Form.Item
              name="dateRange"
              label="Date Range ( Optional )"
              // rules={[
              //   { required: true, message: "Please select date range." },
              // ]}
            >
              <RangePicker className="w-full py-2" format="DD-MM-YYYY" />
            </Form.Item>
          </div>

          <div style={{ marginTop: 24 }} className="ant-select-selector-white">
            <Form.Item
              name="masterIb"
              label="Master IB"
              rules={[
                { required: true, message: "Please select Master IB option." },
              ]}
            >
              <Select
                size="large"
                options={[
                  { label: "No", value: false },
                  { label: "Yes", value: true },
                ]}
              />
            </Form.Item>
          </div>

          <div className="flex justify-end items-center gap-3 mt-6">
            <button
              type="button"
              onClick={handleCloseReportModal}
              disabled={isReportLoading}
              className="bg-secondary text-sm text-white font-semibold rounded-lg px-5 py-2"
            >
              Cancel
            </button>
            <LoadableButton
              type="submit"
              className="bg-primary text-sm text-white font-semibold rounded-lg px-5 py-2"
              lable="Get Hierarchy Commission Report"
              isLoading={isReportLoading}
              loadingLable="Getting report..."
            />
          </div>
        </Form>
      </ModalComponent>
    </div>
  );
};

export default AssignedToSales;
